import camelCase from 'lodash/camelCase'
import upperFirst from 'lodash/upperFirst'

export type FormiePosition = 'verbb\\formie\\positions\\AboveInput' | 'verbb\\formie\\positions\\BelowInput' | 'verbb\\formie\\positions\\Hidden'

export type FormieFieldTypename =
  | 'Field_Agree'
  | 'Field_Checkboxes'
  | 'Field_Date'
  | 'Field_Dropdown'
  | 'Field_Email'
  | 'Field_Entries'
  | 'Field_FileUpload'
  | 'Field_Heading'
  | 'Field_Hidden'
  | 'Field_Html'
  | 'Field_MultiLineText'
  | 'Field_Name'
  | 'Field_Number'
  | 'Field_Phone'
  | 'Field_Radio'
  | 'Field_Section'
  | 'Field_SingleLineText'
  | 'Field_Group'
  | 'Field_Repeater'

export interface FormieField {
  __typename: FormieFieldTypename | string
  inputTypeName: string
  handle: string
  name: string
  labelPosition?: FormiePosition | string
  headingSize?: string
  addLabel?: string
  placeholder?: string
  instructions?: string
  instructionsPosition?: FormiePosition | string
  visibility?: 'disabled' | 'hidden' | string
  required?: boolean
  errorMessage?: string
  minLength?: number
  minType?: string
  maxLength?: number
  maxType?: string
  minValue?: number | string
  maxValue?: number | string
  limit?: boolean
  defaultDate?: string
  maxDate?: string
  minDate?: string
  minRows?: number
  maxRows?: number
  options?: Array<{ label: string; value: string; isDefault?: boolean }>
  entries?: Array<{ id: string | number; title: string }>
  allowedKinds?: string[]
  multi?: boolean
  rows?: Array<any>
  useMultipleFields?: boolean
  defaultOption?: string
  defaultValue?: any
  defaultState?: boolean
  sizeMinLimit?: number
  sizeLimit?: number
  limitFiles?: number | string
  layout?: 'horizontal' | 'vertical' | string
  htmlContent?: string
  borderWidth?: number
  borderStyle?: string
  borderColor?: string
  prefixEnabled?: boolean
  prefixDefaultValue?: string
  prefixErrorMessage?: string
  predixLabel?: string
  prefixOptions?: Array<{ label: string; value: string }>
  prefixPlaceholder?: string
  prefixRequired?: boolean
  firstNameEnabled?: boolean
  firstNameDefaultValue?: string
  firstNameErrorMessage?: string
  firstNameLabel?: string
  firstNamePlaceholder?: string
  firstNameRequired?: boolean
  middleNameEnabled?: boolean
  middleNameDefaultValue?: string
  middleNameErrorMessage?: string
  middleNameLabel?: string
  middleNamePlaceholder?: string
  middleNameRequired?: boolean
  lastNameEnabled?: boolean
  lastNameDefaultValue?: string
  lastNameErrorMessage?: string
  lastNameLabel?: string
  lastNamePlaceholder?: string
  lastNameRequired?: boolean
}

export interface FormieSettings {
  displayFormTitle?: boolean
  displayCurrentPageTitle?: boolean
  defaultLabelPosition?: FormiePosition | string
  defaultInstructionsPosition?: FormiePosition | string
  errorMessageHtml?: string
  redirectUrl?: string
  submitAction?: 'reload' | 'entry' | 'url' | 'message' | string
  submitActionFormHide?: boolean
  submitActionMessageHtml?: string
  submitActionMessageTimeout?: number
  submitActionTab?: 'new-tab' | 'same-tab' | string
  loadingIndicator?: boolean
  loadingIndicatorText?: string
  combinedSizeLimit?: number
}

export function fieldComponentName(field: FormieField) {
  return upperFirst(camelCase(`Formie ${field.__typename}`))
}

export function fieldVisible(field: FormieField) {
  return !['disabled', 'hidden'].includes(field.visibility || '')
}

export function fieldName(field: FormieField, parentField?: FormieField) {
  return `__${field.__typename}${parentField ? `__${parentField.handle}` : ''}__${field.handle}`
}

const labelPositionMap = {
  'verbb\\formie\\positions\\AboveInput': 'before',
  'verbb\\formie\\positions\\BelowInput': 'after',
  'verbb\\formie\\positions\\Hidden': 'hidden',
}

export function fieldLabelPosition(settings: FormieSettings, field: FormieField, fallback = 'before') {
  return labelPositionMap[field.labelPosition as keyof typeof labelPositionMap] ?? labelPositionMap[settings.defaultLabelPosition as keyof typeof labelPositionMap] ?? fallback
}

const helpPositionMap = {
  'verbb\\formie\\positions\\AboveInput': 'before',
  'verbb\\formie\\positions\\BelowInput': 'after',
}

export function fieldHelpPosition(settings: FormieSettings, field: FormieField, fallback = 'before') {
  return (
    helpPositionMap[field.instructionsPosition as keyof typeof helpPositionMap] ?? helpPositionMap[settings.defaultInstructionsPosition as keyof typeof helpPositionMap] ?? fallback
  )
}

export function fieldValidationArray(field: FormieField) {
  return [field.required ? ['required', 'trim'] : ['optional']]
}

export function fieldTextValidationArray(field: FormieField, hardMaxLength: number | false = false) {
  // todo: minType and maxType can be 'characters' or 'words', we do not yet support 'words'
  const minLength = typeof field.minLength === 'number' && field.minType! === 'characters' ? field.minLength : undefined
  const maxLength =
    typeof field.maxLength === 'number' && field.maxType! === 'characters' && (!hardMaxLength || field.maxLength < hardMaxLength)
      ? field.maxLength
      : hardMaxLength
        ? hardMaxLength
        : undefined

  if (!minLength && !maxLength) {
    return []
  }

  // FormKit tuple format: ['length', min, max]
  return maxLength !== undefined ? [['length', minLength !== undefined ? minLength : 0, maxLength]] : [['length', minLength]]
}

export function fieldNumberValidationArray(field: FormieField) {
  const minValue = typeof field.minValue === 'number' ? field.minValue : typeof field.minValue === 'string' ? parseInt(field.minValue, 10) : undefined
  const maxValue = typeof field.maxValue === 'number' ? field.maxValue : typeof field.maxValue === 'string' ? parseInt(field.maxValue, 10) : undefined
  return [
    ['number'],
    field.limit && typeof minValue === 'number' && typeof maxValue === 'number' ? ['between', minValue, maxValue] : undefined,
    field.limit && typeof minValue === 'number' && typeof maxValue !== 'number' ? ['min', minValue] : undefined,
    field.limit && typeof minValue !== 'number' && typeof maxValue === 'number' ? ['max', maxValue] : undefined,
  ].filter((value) => value !== undefined)
}

export function fieldOptions(field: FormieField) {
  return field.options ? Object.fromEntries(field.options.map(({ label, value }) => [value, label])) : {}
}

export function fieldDefaultOptions(field: FormieField) {
  return field.options ? field.options.filter(({ isDefault }) => isDefault).map(({ value }) => value) : []
}

export function fieldHiddenDefaultValue(field: FormieField) {
  switch (field.defaultOption) {
    case 'currentUrl':
      return useRoute().fullPath

    case 'currentUrlNoQueryString':
      return useRoute().path

    default:
      return field.defaultValue
  }
}
